'use client'
import { createContext, useMemo, useState } from 'react'

import themeConfig from '@configs/themeConfig'
import demoConfigs from '@configs/demoConfigs'
import primaryColorConfig from '@configs/primaryColorConfig'

import { useObjectCookie } from '@core/hooks/useObjectCookie'

export const SettingsContext = createContext(null)

export const SettingsProvider = props => {
  const demoName = props.demoName || null
  const demoConfigurations = demoName ? demoConfigs[demoName] : {}

  const initialSettings = {
    superadmin: false,
    accesstoken: '',
    billdate: '',
    type: '',
    selectedUser: {},
    branchFamily: [],
    allowCards: {},
    allowPages: {},
    permissionsLoaded: false,
    branch: '',
    family: '',
    mode: themeConfig.mode,
    skin: themeConfig.skin,
    semiDark: themeConfig.semiDark,
    layout: themeConfig.layout,
    navbarContentWidth: themeConfig.navbar.contentWidth,
    contentWidth: themeConfig.contentWidth,
    footerContentWidth: themeConfig.footer.contentWidth,
    primaryColor: primaryColorConfig[0].main,
    ...(demoName && demoConfigurations)
  }

  const updatedInitialSettings = {
    ...initialSettings,
    mode: props.mode || (demoName && demoConfigurations.mode) || themeConfig.mode
  }

  const [settingsCookie, updateSettingsCookie] = useObjectCookie(
    demoName ? themeConfig.settingsCookieName.replace('demo-1', demoName) : themeConfig.settingsCookieName,
    JSON.stringify(props.settingsCookie) !== '{}' ? props.settingsCookie : updatedInitialSettings
  )

  // One-time migration off the retired teal default (the Aug 2026 "glassy-mint"
  // theme). The accent is stored per-user in the settings cookie and spreads over
  // the palette default below, so a value saved while teal was the default
  // outlives the revert. Gated on themeRev so this runs once: #0D9394 is also
  // primary-2, a legitimate swatch that predates that theme, and an unconditional
  // snap would make it permanently unselectable.
  const THEME_REV = 2
  const RETIRED_PRIMARY = '#0d9394'
  const migratePrimary = s => {
    if (!s || s.themeRev >= THEME_REV) return s

    return {
      ...s,
      themeRev: THEME_REV,
      ...(String(s.primaryColor).toLowerCase() === RETIRED_PRIMARY
        ? { primaryColor: primaryColorConfig[0].main }
        : {})
    }
  }

  const [_settingsState, _updateSettingsState] = useState(
    JSON.stringify(settingsCookie) !== '{}' ? migratePrimary({
      ...updatedInitialSettings,
      ...settingsCookie,
      permissionsLoaded: false
    }) : updatedInitialSettings
  )

  const getCookieSafeSettings = settings => {
    return {
      superadmin: settings.superadmin,
      accesstoken: settings.accesstoken,
      billdate: settings.billdate,
      type: settings.type,
      selectedUser: settings.selectedUser,
      branch: settings.branch,
      family: settings.family,
      mode: settings.mode,
      skin: settings.skin,
      semiDark: settings.semiDark,
      layout: settings.layout,
      navbarContentWidth: settings.navbarContentWidth,
      contentWidth: settings.contentWidth,
      footerContentWidth: settings.footerContentWidth,
      primaryColor: settings.primaryColor,
      themeRev: settings.themeRev
    }
  }

  const updateSettings = (settings, options) => {
    const { updateCookie = true, cookieMode = 'safe' } = options || {}

    _updateSettingsState(prev => {
      const newSettings = { ...prev, ...settings }

      if (updateCookie) {
        updateSettingsCookie(cookieMode === 'full' ? newSettings : getCookieSafeSettings(newSettings))
      }

      return newSettings
    })
  }

  const updatePageSettings = settings => {
    updateSettings(settings, { updateCookie: false })

    return () => updateSettings(settingsCookie, { updateCookie: false })
  }

  const resetSettings = () => {
    updateSettings(initialSettings)
  }

  const isSettingsChanged = useMemo(
    () => JSON.stringify(initialSettings) !== JSON.stringify(_settingsState),
    [_settingsState]
  )

  return (
    <SettingsContext.Provider
      value={{
        settings: _settingsState,
        updateSettings,
        isSettingsChanged,
        resetSettings,
        updatePageSettings
      }}
    >
      {props.children}
    </SettingsContext.Provider>
  )
}
